import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { TranslateOptions, useI18n } from 'vue-i18n'
import { DynCategories, TNKeys } from '@/components/lang/langHandler'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function useTranslateKey() {
  const { t, te } = useI18n()
  /**
   * Translates the given key using vue-i18n.
   * If no translation is available, returns the original value.
   * Wrapper for useI18n
   *
   * @param value - The translation key.
   * rest of params imitated the useI18n
   * @returns The translated key if available, otherwise the original value.
   * @author fw, gk
   */
  const translateKey = (
    value: TNKeys,
    named?: Record<string, string | number>,
    options?: TranslateOptions<string>
  ) => {
    if (!value) return ''
    return t(value, named, options)
  }

  const translateKeyDyn = <S extends string>(
    category: DynCategories,
    value: string extends S ? S : 'MUST NOT USE STRING LITERAL FOR DYN',
    named?: Record<string, string | number>,
    options?: TranslateOptions<string>
  ) => {
    if (!value) return ''
    const sanitizedCategory = String(category)
      .replace(/[^\p{L}_.]/gu, '')
      .toLowerCase()
    const sanitizedKey = value.replace(/[^\p{L}_.]/gu, '').toLowerCase()
    const cleanedKey = `dyn.${sanitizedCategory}.${sanitizedKey}`
    // Check if translation exists, if not return the original unsanitized value
    if (!te(cleanedKey)) {
      return value
    }
    return t(cleanedKey, named, options)
  }

  return { translateKey, translateKeyDyn }
}

type UseTranslateKeyRet = ReturnType<typeof useTranslateKey>
export type TranslateKeyFn = UseTranslateKeyRet['translateKey']

export function removeTeilPromptingPlaceholders(html: string): string {
  const asyncPlaceholderPattern = /<async-placeholder[^>]*>.*?<\/async-placeholder>/gs

  const decodeEntities = (input: string) =>
    input
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')

  return html.replace(asyncPlaceholderPattern, match => {
    const attrMatch = match.match(/data-content-snapshot=(['"])(.*?)\1/i)
    if (!attrMatch) return match
    return decodeEntities(attrMatch[2])
  })
}

export const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}
